import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Target, CheckCircle, Award, Bell } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StartJourneyPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [name, setName] = useState('');
  const [trialInfo, setTrialInfo] = useState(null);
  const [timeLeft, setTimeLeft] = useState(48); // 48 hours for bonus

  // Countdown timer for 48-hour bonus
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 0) return 0;
        return prev - 0.1; // Decrease by 0.1 hour (6 minutes)
      });
    }, 360000); // Update every 6 minutes

    return () => clearInterval(timer);
  }, []);

  const formatTimeLeft = () => {
    const hours = Math.floor(timeLeft);
    return `${hours} hours`;
  };

  const handleStartTrial = async () => {
    if (!email || !company || !name) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      // Start trial with backend tracking
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trial/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          email: email,
          name: name,
          company: company
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Store trial info with 48h bonus timestamp
        const bonusExpiry = new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString();
        localStorage.setItem('trialEmail', email);
        localStorage.setItem('trialInfo', JSON.stringify(data));
        localStorage.setItem('bonusExpiry', bonusExpiry);
        
        setTrialInfo(data);
        
        // Navigate to calculator
        navigate('/business-case');
      } else {
        alert(data.detail || 'Failed to start trial');
      }
      
    } catch (error) {
      console.error('Trial start error:', error);
      alert('Failed to start trial. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-600 via-blue-700 to-purple-800">
      <div className="container mx-auto px-6 py-24">
        <div className="max-w-6xl mx-auto">
          
          {/* Main Headline */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-tight">
              Start Your Journey Here
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 leading-relaxed max-w-4xl mx-auto">
              Be among the first manufacturers to unlock hidden profit potential with authentic QRM methodology. 
              Stop guessing, start calculating your competitive advantage.
            </p>
          </div>

          {/* Free vs Paid Options */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            
            {/* Limited Free Trial Option */}
            <Card className="p-8 bg-white/95 backdrop-blur-sm shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-10 w-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Free Trial</h2>
                <p className="text-green-600 font-semibold text-lg">Try Each Tool Once • No Credit Card</p>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                  <span><strong>1 EBITDA calculation</strong> with sector intelligence</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                  <span><strong>1 Financial Lab analysis</strong> with competitive benchmarking</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                  <span><strong>1 Strategic consulting</strong> session</span>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 h-5 w-5 mt-0.5 text-blue-500">🔒</div>
                  <span className="text-gray-600"><strong>Privacy guarantee:</strong> Your data deleted within 48h</span>
                </li>
              </ul>
              
              <Button 
                size="lg"
                className="w-full py-4 bg-green-600 hover:bg-green-700 text-white font-bold text-lg"
                onClick={() => navigate('/business-case')}
              >
                <Target className="mr-2 h-5 w-5" />
                Start Free Trial (1 Use Per Tool)
              </Button>
              
              <p className="text-center text-gray-600 text-sm mt-3">
                Use each tool once • No time limit • Upgrade for unlimited access
              </p>
            </Card>

            {/* Premium Pricing Options */}
            <Card className="p-8 bg-white/95 backdrop-blur-sm shadow-2xl border-2 border-purple-300">
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-10 w-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Premium Unlimited</h2>
                <p className="text-purple-600 font-semibold text-lg">Choose Your Plan</p>
              </div>
              
              {/* Coming Soon Banner */}
              <div className="mb-6 p-6 bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-purple-300 rounded-xl">
                <div className="text-center">
                  <div className="inline-block px-4 py-2 bg-purple-600 text-white font-bold rounded-full text-sm mb-3">
                    🚀 COMING SOON
                  </div>
                  <h3 className="text-xl font-bold text-purple-800 mb-2">Premium Subscriptions Launching Soon!</h3>
                  <p className="text-purple-700 mb-4">
                    We're setting up secure payment processing. Leave your email to be notified when Premium plans become available.
                  </p>
                </div>
              </div>
              
              {/* Pricing Preview */}
              <div className="mb-6 opacity-75">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 p-3 text-left font-bold">Plan</th>
                      <th className="border border-gray-300 p-3 text-left font-bold">Price</th>
                      <th className="border border-gray-300 p-3 text-left font-bold">Bonus</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 p-3"><strong>Monthly</strong></td>
                      <td className="border border-gray-300 p-3">€200/month</td>
                      <td className="border border-gray-300 p-3 text-green-700"><strong>1 hour free consulting</strong></td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-3"><strong>Annual</strong></td>
                      <td className="border border-gray-300 p-3">€2,000/year</td>
                      <td className="border border-gray-300 p-3 text-green-700"><strong>3 hours free consulting</strong></td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-center text-sm text-gray-500 mt-2">Annual plan = 2 months free (€2,400 value for €2,000)</p>
              </div>
              
              <ul className="space-y-3 mb-8 opacity-75">
                <li className="flex items-start">
                  <Award className="mr-2 h-5 w-5 text-purple-500 mt-0.5" />
                  <span><strong>Unlimited EBITDA calculations</strong> for all scenarios</span>
                </li>
                <li className="flex items-start">
                  <Award className="mr-2 h-5 w-5 text-purple-500 mt-0.5" />
                  <span><strong>Unlimited Financial Lab</strong> competitive intelligence</span>
                </li>
                <li className="flex items-start">
                  <Award className="mr-2 h-5 w-5 text-purple-500 mt-0.5" />
                  <span><strong>Unlimited Strategic consulting</strong> access</span>
                </li>
                <li className="flex items-start">
                  <Award className="mr-2 h-5 w-5 text-purple-500 mt-0.5" />
                  <span><strong>Personal consultation with Jan Pauwels</strong></span>
                </li>
              </ul>
              
              <div className="space-y-3">
                <Button 
                  size="lg"
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold text-lg"
                  onClick={() => window.open('mailto:info@smartqrmnavigator.com?subject=Notify Me - Premium Plan Interest&body=Hello Jan,\n\nI am interested in the Premium subscription plans. Please notify me when they become available.\n\nCOMPANY INFO:\n• Company: [Your Company]\n• Name: [Your Name]\n• Email: [Your Email]\n\nI am interested in:\n• [ ] Monthly Plan (€200/month)\n• [ ] Annual Plan (€2,000/year)\n\nBest regards', '_blank')}
                >
                  <Bell className="mr-2 h-5 w-5" />
                  Notify Me When Available
                </Button>
              </div>
              
              <div className="text-center mt-4">
                <p className="text-purple-700 text-sm font-medium">
                  💡 Meanwhile, enjoy your free trial with 1 use per tool!
                </p>
                <p className="text-gray-600 text-xs mt-1">
                  Contact info@smartqrmnavigator.com for early access
                </p>
              </div>
            </Card>
          </div>

          {/* Privacy Promise */}
          <div className="text-center mb-8 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
            <p className="text-blue-100 text-lg">
              🔒 <strong>Privacy Promise:</strong> Your data will be deleted within 48h - completely GDPR compliant
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default StartJourneyPage;
